<script setup lang="ts">
import { computed, defineEmits, defineProps, ref } from "vue";
import TextInput from "@/components/inputs/TextInput.vue";
import { AuthModalMode } from "@/types/auth";
import { useI18n } from "vue-i18n";
import LoaderIcon from "@/components/icons/LoaderIcon.vue";
import ConfirmButton from "@/views/Auth/components/ConfirmButton.vue";

const props = defineProps<{
  waitApiCall: boolean;
}>();
const emits = defineEmits<{
  (e: "changeModalMode", value: string): void;
  (e: "login", email: string, password: string): void;
}>();

const { t } = useI18n();

const email = ref<string>("");
const password = ref<string>("");

const isCredentialsValid = computed(() => {
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return (
    emailRe.test(email.value) && password.value && password.value.length > 0
  );
});

function handleLogin() {
  if (!isCredentialsValid.value) {
    return;
  }
  emits("login", email.value, password.value);
}

function handleChangeMode(newMode: AuthModalMode) {
  if (props.waitApiCall) {
    return;
  }

  email.value = "";
  password.value = "";
  emits("changeModalMode", newMode);
}
</script>

<template>
  <TextInput
    :label="t('auth.email')"
    v-model="email"
    type="email"
    class="mb-4"
  />
  <TextInput
    :label="t('auth.password')"
    v-model="password"
    type="password"
    class="mb-4"
  />
  <div class="mb-10 flex">
    <p class="link-text" @click="handleChangeMode('forgot')">
      {{ t("auth.forgot-password") }}
    </p>
  </div>
  <ConfirmButton
    :disabled="!isCredentialsValid || waitApiCall"
    :loading="waitApiCall"
    :button-text="t('buttons.auth.login')"
    @click="handleLogin"
  />
  <div class="flex items-center justify-center gap-2">
    <p class="text-sm text-black select-none">
      {{ t("auth.dont-have-an-account") }}
    </p>
    <p class="link-text" @click="handleChangeMode('signup')">
      {{ t("auth.sign-up") }}
    </p>
  </div>
</template>

<style scoped>
.link-text {
  @apply underline text-active text-sm select-none cursor-pointer;
}
</style>
